<script setup lang="ts">
import { computed, reactive, ref, watch, type PropType } from 'vue';
import type { ContractTypeInDb } from '@/api';
import { useContractTypeStore } from '@/stores/contractTypeStore';
import { useGaiaToast } from '@/composables/useGaiaToast';
import { logger } from '@/utils/logger';
import GaiaButton from '../../base/GaiaButton.vue';
import GaiaFormField from '../../base/GaiaFormField.vue';
import GaiaModal from '../../base/GaiaModal.vue';
import GaiaInput from '../../base/inputs/GaiaInput.vue';
import GaiaTextarea from '../../base/inputs/GaiaTextarea.vue';
import GaiaBanner from '../../composite/GaiaBanner.vue';

/**
 * Create or edit one of the organisation's contract types. Without
 * `contractType` it creates; with one it edits that type.
 */
const props = defineProps({
  show: {
    type: Boolean,
    required: true,
  },
  contractType: {
    type: Object as PropType<ContractTypeInDb | null>,
    default: null,
  },
});

const emit = defineEmits<{ 'update:show': [value: boolean] }>();

const contractTypeStore = useContractTypeStore();
const message = useGaiaToast();

const form = reactive({ name: '', description: '' });
const attempted = ref(false);
const saving = ref(false);
const saveError = ref<string | null>(null);

const isEdit = computed(() => props.contractType !== null);

const errors = computed(() => ({
  name: form.name.trim() ? undefined : 'Enter a name',
  description: form.description.trim() ? undefined : 'Describe what documents this type covers',
}));

// Re-seed on every open: the modal is reused for create and edit.
watch(
  () => props.show,
  show => {
    if (!show) return;
    form.name = props.contractType?.name ?? '';
    form.description = props.contractType?.description ?? '';
    attempted.value = false;
    saveError.value = null;
  },
  { immediate: true }
);

async function save(): Promise<void> {
  attempted.value = true;
  if (errors.value.name || errors.value.description) return;
  saving.value = true;
  saveError.value = null;
  const body = { name: form.name.trim(), description: form.description.trim() };
  try {
    const saved = props.contractType
      ? await contractTypeStore.updateContractType(props.contractType.uid, body)
      : await contractTypeStore.createContractType(body);
    message.success(isEdit.value ? `${saved.name} saved.` : `${saved.name} created.`);
    emit('update:show', false);
  } catch (error) {
    logger.error('Failed to save contract type', error);
    saveError.value = error instanceof Error ? error.message : 'Failed to save the contract type.';
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <gaia-modal
    :show="props.show"
    width="480px"
    :title="isEdit ? 'Edit contract type' : 'Create contract type'"
    :mask-closable="!saving"
    :close-on-esc="!saving"
    @update:show="(value: boolean) => emit('update:show', value)"
  >
    <n-flex vertical :size="14" :wrap="false">
      <gaia-banner v-if="saveError" type="error">{{ saveError }}</gaia-banner>
      <gaia-form-field label="Name" required :error="attempted ? errors.name : undefined">
        <gaia-input v-model:value="form.name" placeholder="e.g. Supply Agreement" />
      </gaia-form-field>
      <gaia-form-field
        label="Description"
        required
        hint="The contract classifier reads this to recognise the type"
        :error="attempted ? errors.description : undefined"
      >
        <gaia-textarea
          v-model:value="form.description"
          placeholder="What documents this type covers"
          :autosize="{ minRows: 3, maxRows: 8 }"
        />
      </gaia-form-field>
    </n-flex>
    <template #action>
      <n-flex justify="end" :size="8" :wrap="false">
        <gaia-button :disabled="saving" @click="emit('update:show', false)">Cancel</gaia-button>
        <gaia-button type="primary" :loading="saving" @click="save">
          {{ isEdit ? 'Save' : 'Create' }}
        </gaia-button>
      </n-flex>
    </template>
  </gaia-modal>
</template>
